/** Pinia Store para la gestión de Carátula */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();
const endPointDirectDebit = "/api/DirectDebitRequests";

export const useDirectDebitStore = defineStore('directDebitRequest', {
    /**
     * Estado central del store.
     */
    state: () => ({
        /**Modal */
        isModalOpen: false,
        isEdit: false,
        loanApplicationsOptions: [],
        domiciliacionContent: null,
        errors: null,
        /**Formulario */
        selectedApplication: null,
        form: {},
        nameFilePDF: "Solicitud_de_domiciliación_Anexo_D.pdf",
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        directDebitsRequest: [],
        statusDirectDebit: 21, //Dato default
    }),
    /**
     * Getters para filtrar, ordenar y paginar las carpatulas.
     */
    getters: {
        /**
         * Devuelve los anexos D filtradas por el texto de búsqueda.
         */
        filteredS: (state) => {
            if (!state.searchString) return state.directDebitsRequest;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.directDebitsRequest.filter(directDebitsRq => {
                const firstNameProspect = directDebitsRq?.prospectus?.generalInfo?.firstName ? directDebitsRq?.prospectus?.generalInfo?.firstName?.toLowerCase() : '';
                const lastNameProspect = directDebitsRq?.prospectus?.generalInfo?.lastName ? directDebitsRq?.prospectus?.generalInfo?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = directDebitsRq?.prospectus?.generalInfo?.mothersMaidenName ? directDebitsRq?.prospectus?.generalInfo?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = directDebitsRq?.prospectus?.generalInfo?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    directDebitsRq?.prospectus?.generalInfo?.businessName ? directDebitsRq?.prospectus?.generalInfo?.businessName?.toLowerCase() : '';

                return (
                    directDebitsRq?.directDebitRequest?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    directDebitsRq?.quotations?.quotationsReferenceNumber?.toLowerCase().includes(searchLower) ||
                    directDebitsRq?.loanApplication?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    directDebitsRq?.prospectus?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    directDebitsRq?.product?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    directDebitsRq?.prospectus?.personType?.peopleTypeName?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(directDebitsRq?.directDebitRequest.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(directDebitsRq?.directDebitRequest?.updatedAt)?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena los anexos D según la clave de orden actual.
         */
        sortedS: (state) => {
            //Logica de ordenamiento
            let sortedList = [...state.filteredS];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.prospectus.generalInfo.businessName === null) {
                                return `${item.prospectus?.generalInfo?.firstName || ''} ${item.prospectus?.generalInfo?.lastName || ''} ${item.prospectus?.generalInfo?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.prospectus?.generalInfo?.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedS: (state) => {
            if (!state.sortedS || state.sortedS.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedS.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene las opciones de solicitudes disponibles para llenar el combo de selección de la modal de anexo d.
         * http://localhost:5228/api/DirectDebitRequests/loanApplication/Detail?company=1 ${userAuth.user.companyId}
         */
        async getLoanApplication() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.directDebitsRequestApi.get(`${endPointDirectDebit}/loanApplication/Detail?company=${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crea un nuevo regisro de Anexo D.
         * @param {*} payload 
         * @returns 
         * http://localhost:5228/api/DirectDebitRequests
        */
        async createdS(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.directDebitsRequestApi.post(endPointDirectDebit, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('directDebitRequest', 'creatingSuccess'));
                    this.form.directDebitRequest = data.data;
                    this.selectedApplication.directDebitRequest = data.data;
                    this.directDebitsRequest.unshift(this.selectedApplication);
                    return data;
                } else {
                    $toast.success(getLocaleMessage('directDebitRequest', 'creatingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Obtiene todos los pagarés registrados.
         * http://localhost:5228/api/DirectDebitRequests/Detail/company/1
        */
        async fetchAnexo() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.directDebitsRequestApi.get(`${endPointDirectDebit}/detail/company/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.directDebitsRequest = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualiza un registro de pagaré existente.
         * http://localhost:5149/api/PromissoryNotes
        */
        async updateS(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.directDebitsRequestApi.put(`${endPointDirectDebit}`, payload);
                if (data && data.status) {
                    const index = this.directDebitsRequest.findIndex(c => c.directDebitRequest.id === data.id);
                    if (index !== -1) {
                        this.directDebitsRequest.splice(index, 1, this.form);
                    }
                    $toast.success(getLocaleMessage('directDebitRequest', 'editingSuccess'));
                    return data;
                } else {
                    $toast.success(getLocaleMessage('directDebitRequest', 'editingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal para crear nuevo registro de carátula.
         * Acción por el boton Generar
         * Abrir modal de creación isEdit = false
         */
        async openModal() {
            this.isModalOpen = true;
            //this.isEdit = false;
            await this.getLoanApplication(); //Refrescar los datos del combo de selección
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isEdit = false;
            this.isModalOpen = false;
            this.loanApplicationsOptions = [];
            this.domiciliacionContent = null;
            this.errors = null;
            this.form = {};
            await this.fetchAnexo(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de un anexo D existente para edición.
         * Abrir modal de edición isEdit = true
         */
        editData(formEdit) {
            this.isEdit = true;
            this.selectedApplication = formEdit;
            this.form = formEdit;
        },
        clearText(data) {
            return data.split('\n').map(s => s.trim()).filter(Boolean).join(' ');
        },
        /**
         * Acción para guarda los datos del formulario, ya sea creando o actualizando.
        */
        async saveData() {
            try {
                useLoadingStore().loading = true;
                
                let dayPay = new Date(this.form?.promissoryNotes?.paymentDate)?.getDate() || new Date();
                let name = this.form?.prospectus?.generalInfo?.businessName === null ?
                    `${this.form?.prospectus?.generalInfo?.firstName} ${this.form?.prospectus?.generalInfo?.lastName}
                    ${this.form?.prospectus?.generalInfo?.mothersMaidenName}` :
                    this.form?.prospectus?.generalInfo?.businessName;

                let docmicilioData = this.form?.prospectus?.personType?.peopleTypeId === 1 ? `
                    ${this.form?.prospectus?.addressAccredited?.street}
                    ${this.form?.prospectus?.addressAccredited?.exteriorNumber}
                    ${this.form?.prospectus?.addressAccredited?.interiorNumber}
                    ${this.form?.prospectus?.addressAccredited?.district}` :
                    `${this.form?.prospectus?.addressLegalRepresentative?.street}
                    ${this.form?.prospectus?.addressLegalRepresentative?.exteriorNumber}
                    ${this.form?.prospectus?.addressLegalRepresentative?.interiorNumber}
                    ${this.form?.prospectus?.addressLegalRepresentative?.district}
                    ` ;

                let payload = {
                    loanApplicationId: this.form.loanApplication.id,
                    statusId: this.statusDirectDebit,
                    placeRequest: this.form.directDebitRequest.placeRequest,
                    dateRequest: this.form.directDebitRequest.dateRequest,
                    providerName: "KVF GROUP SA DE CV.",
                    creditToPay: this.clearText(this.form?.product?.description),
                    authorizedData: this.clearText(docmicilioData),
                    frequency: "",
                    collectionDays: dayPay.toString(),
                    authorizedAmount: this.form.quotations.creditAmount.toString(),
                    bank: this.form.quotations.directDebits.bank,
                    accountNumber: this.form.quotations.directDebits.accountNumber,
                    clabe: this.form.quotations.directDebits.clabe,
                    term: this.form.quotations.totalTerm,
                    borrowersName: this.clearText(name),
                    startDate: this.form.quotations.disbursementDate
                };

                let response;
                if (this.isEdit) {
                    payload.id = this.form.directDebitRequest.id;
                    response = await this.updateS(payload);
                } else {
                    response = await this.createdS(payload);
                }

                if (response && response.status) {
                    this.downloadPDF();
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad para manejar la descarga de un pdf
         * @returns Descarga un PDF
         */
        async downloadPDF() {
            if (!this.domiciliacionContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            const element = document.querySelector('.page');
            if (element) element.classList.add('pdf-mode');

            const opt = {
                margin: 5,
                filename: this.nameFilePDF,
                image: { type: 'jpeg', quality: 1 },
                html2canvas: {
                    scale: 2,
                    logging: true,
                    useCORS: true,
                    letterRendering: true,
                    clip: false, // Permite capturar contenido fuera de los límites del viewport
                    ignoreElements: function (element) {
                        return false; // Captura todos los elementos
                    }
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4', // Tamaño A4 en píxeles
                    orientation: 'portrait'
                },
                // Configuración para manejar saltos de página
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy']
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.domiciliacionContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;

                /**Restableciendo diseños de origen en la modal */
                if (formActions) formActions.style.display = 'block';
                if (element) element.classList.remove('pdf-mode');
            }
        },
        setContentS(ref) {
            this.domiciliacionContent = ref;
        }
    }
});